import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Drawer, Modal, Typography, message } from 'antd';
import { ExclamationCircleOutlined, SearchOutlined, TeamOutlined } from '@ant-design/icons';
import { buildCompletedProposalsForMarcacao } from '../utils/proposalRequestsDisplay';
import cccApi, {
  ProspeccaoLead as ApiProspeccaoLead,
  type AssinadoDadosData,
  type EnvioPropostaData,
  type FidelizadosDadosData,
  type TentativasContactoData,
  type WorkspaceMotivoData,
} from '../api/cccApi';
import CccCalendarEventModal from '../components/CccCalendarEventModal';
import type { CccCalendarClientOption } from '../api/cccCalendarApi';
import { defaultCalendarSlotTimes } from '../utils/calendarClientFromMarcacao';
import CccGestaoModalsOverlay, { type CccGestaoModalKind } from '../components/CccGestaoModalsOverlay';
import CccPedirAssinaturaDigitalOverlay from '../components/CccPedirAssinaturaDigitalOverlay';
import CccAlterarComercialModal from '../components/CccAlterarComercialModal';
import CccBulkSelectionBar from '../components/CccBulkSelectionBar';
import CccProspeccaoLeadHistory from '../components/CccProspeccaoLeadHistory';
import CccSolicitarPropostaProspeccaoOverlay from '../components/CccSolicitarPropostaProspeccaoOverlay';
import CccSubmeterContratoProspeccaoOverlay from '../components/CccSubmeterContratoProspeccaoOverlay';
import {
  isValidMarcacaoId,
  resolveProspeccaoMarcacaoId,
} from '../config/prospeccaoMarcacao';
import RecuperacaoComercialRequests from '../components/RecuperacaoComercialRequests';
import ProspeccaoTentativasContactoForm from '../components/ProspeccaoTentativasContactoForm';
import ProspeccaoWorkspaceMotivo from '../components/ProspeccaoWorkspaceMotivo';
import ProspeccaoFidelizadosForm from '../components/ProspeccaoFidelizadosForm';
import ProspeccaoEnvioPropostaForm from '../components/ProspeccaoEnvioPropostaForm';
import ProspeccaoAssinadoForm from '../components/ProspeccaoAssinadoForm';
import { RecuperacaoMotivoWarningIcon } from '../components/RecuperacaoMotivoWarning';
import RecuperacaoInvestigateModal, {
  type RecuperacaoInvestigateQuickSearchSeed,
  type RecuperacaoInvestigateRow,
} from '../components/RecuperacaoInvestigateModal';
import CccWorkspaceDuplicateIndicator, {
  type CccWorkspaceDuplicateFlags,
} from '../components/CccWorkspaceDuplicateIndicator';
import WonderTradeLoader from '@crm/components/Shared/WonderTradeLoader';
import apiService from '@crm/services/api';
import { useCccAuth } from '../contexts/CccAuthContext';
import { isCccCoordenadorOrAdmin } from '../utils/cccUserManagement';
import { isPlatformAdmin } from '../utils/isPlatformAdmin';
import {
  PROSPECCAO_DEFAULT_SUBSTAGE,
  PROSPECCAO_SECTIONS,
  PROSPECCAO_SUBSTAGE_AGUARDAR_PROPOSTA_BO,
  PROSPECCAO_SUBSTAGE_CONTRATO_SUBMETIDO,
  ProspeccaoSectionKey,
  normalizeProspeccaoSubstage,
  prospeccaoSectionForSubstage,
  prospeccaoSubstageLabel,
} from '../config/prospeccaoWorkspace';
import { buildPedidoPropostaLeadDetailsLayout } from '../utils/recuperacaoMarcacaoDisplay';
import {
  hasCccPedidoProposta,
  resolveLeadProposalStatusDisplay,
} from '../utils/proposalRequestsDisplay';
import type { RecuperacaoMarcacao } from '../api/cccApi';
import {
  canNavigateToMatch,
  fetchCccWorkspaceSearch,
  showCccSearchNotFound,
  showCccSearchSuccess,
} from '../utils/cccWorkspaceSearch';
import {
  isProspeccaoNaoAtendidoSubstage,
  isTentativasContactoIncomplete,
  TENTATIVAS_CONTACTO_WARNING_TEXT,
} from '../config/tentativasContacto';
import { isAssinadoSubstage } from '../config/assinadoDados';
import { isEnvioPropostaSubstage } from '../config/envioProposta';
import {
  FIDELIZADOS_WARNING_TEXT,
  isFidelizadosDadosIncomplete,
  isFidelizadosSubstage,
} from '../config/fidelizadosDados';
import {
  getMotivoVariantForSubstage,
  isMotivoTabSubstage,
  isWorkspaceMotivoIncomplete,
  MOTIVO_WARNING_TEXT,
  PENDENTE_MOTIVO_WARNING_TEXT,
  SEM_INTERESSE_MOTIVO_WARNING_TEXT,
  WorkspaceMotivoVariant,
} from '../config/workspaceMotivos';
import {
  CREATION_DATE_FILTERS,
  type CreationDatePresetKey,
  matchesCreationDateFrom,
  parseCreatedAt,
  presetCutoffPickerValue,
} from '../utils/workspaceCreationDateFilter';
import '../styles/recuperacao.css';
import '../styles/prospeccao.css';
import '../styles/recuperacaoInvestigate.css';

const PROSPECCAO_PRODUTO_OPTIONS = ['Solar', 'Luz', 'Gás'] as const;
type ProspeccaoProdutoOption = (typeof PROSPECCAO_PRODUTO_OPTIONS)[number];

const normalizeProspeccaoProduto = (raw: unknown): ProspeccaoProdutoOption[] => {
  if (!Array.isArray(raw)) return [];
  return PROSPECCAO_PRODUTO_OPTIONS.filter((opt) => raw.includes(opt));
};

const toggleProspeccaoProduto = (
  current: ProspeccaoProdutoOption[],
  value: ProspeccaoProdutoOption
): ProspeccaoProdutoOption[] =>
  current.includes(value) ? current.filter((item) => item !== value) : [...current, value];

const ProspeccaoProdutoField: React.FC<{
  value: ProspeccaoProdutoOption[];
  onChange: (next: ProspeccaoProdutoOption[]) => void;
  labelClassName?: string;
  fullWidth?: boolean;
}> = ({ value, onChange, labelClassName = 'prosp-create-label-title', fullWidth = false }) => (
  <div className={fullWidth ? 'prospeccao-create-field prospeccao-create-field--full' : 'prospeccao-create-field'}>
    <span className={labelClassName}>Produto</span>
    <div className="prosp-produto-options">
      {PROSPECCAO_PRODUTO_OPTIONS.map((opt) => (
        <label key={opt} className="rec-envio-proposta-check prosp-produto-check">
          <input
            type="checkbox"
            checked={value.includes(opt)}
            onChange={() => onChange(toggleProspeccaoProduto(value, opt))}
          />
          {opt}
        </label>
      ))}
    </div>
  </div>
);

type ProspeccaoLead = {
  id: string;
  company: string;
  contactName: string;
  phone: string;
  email: string;
  googleMapsLink: string;
  produto: ProspeccaoProdutoOption[];
  substage: string;
  createdAt: string;
  updatedAt: string;
  createdBy: number | null;
  assignedTo: number | null;
  tentativasContacto: TentativasContactoData | null;
  semInteresseMotivo: WorkspaceMotivoData | null;
  pendenteMotivo: WorkspaceMotivoData | null;
  fidelizadosDados: FidelizadosDadosData | null;
  envioProposta: EnvioPropostaData | null;
  assinadoDados: AssinadoDadosData | null;
  proposalRequests: Record<string, unknown>[];
  marcacaoFormData: Record<string, unknown>;
  marcacaoId: string | null;
};

type LeadInfoTab =
  | 'dados'
  | 'motivo'
  | 'fidelizados'
  | 'tentativas_contacto'
  | 'envio_proposta'
  | 'assinado'
  | 'historico'
  | 'pedido';

type LeadNote = {
  id?: string;
  text: string;
  at: string;
  by_id?: number;
  by_name?: string;
  updated_at?: string;
  updated_by_name?: string;
};

const notesFromApiLead = (raw: ApiProspeccaoLead): LeadNote[] => {
  const list = Array.isArray(raw.notes) ? raw.notes : [];
  return list
    .filter((item) => item && typeof item.text === 'string' && item.text.trim())
    .map((item) => ({
      id: item.id ? String(item.id) : undefined,
      text: String(item.text).trim(),
      at: String(item.at || new Date().toISOString()),
      by_id: item.by_id != null ? Number(item.by_id) : undefined,
      by_name: item.by_name ? String(item.by_name) : undefined,
      updated_at: item.updated_at ? String(item.updated_at) : undefined,
      updated_by_name: item.updated_by_name ? String(item.updated_by_name) : undefined,
    }));
};

const buildLeadNotesMap = (rows: ApiProspeccaoLead[]): Record<string, LeadNote[]> => {
  const next: Record<string, LeadNote[]> = {};
  for (const raw of rows) {
    const id = String(raw.id || '').trim();
    if (!id) continue;
    next[id] = notesFromApiLead(raw);
  }
  return next;
};

type LeadDraft = Omit<ProspeccaoLead, 'id' | 'substage' | 'createdAt' | 'updatedAt'>;
type WorkspaceUserOption = { id: number; name: string; username?: string; email?: string };

const { Text } = Typography;

const emptyDraft: LeadDraft = {
  company: '',
  contactName: '',
  phone: '',
  email: '',
  googleMapsLink: '',
  produto: [],
};

const makeId = () => `prosp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const allWorkspaceSubstages = PROSPECCAO_SECTIONS.flatMap((section) =>
  section.subsections.map((sub) => sub.substage)
);

const isValidSubstage = (value?: string): boolean =>
  Boolean(value && allWorkspaceSubstages.includes(normalizeProspeccaoSubstage(value)));

const marcacaoFormDataFromApiLead = (raw: ApiProspeccaoLead): Record<string, unknown> => {
  if (raw.marcacao_form_data && typeof raw.marcacao_form_data === 'object') {
    return raw.marcacao_form_data as Record<string, unknown>;
  }
  const proposals = Array.isArray(raw.proposal_requests) ? raw.proposal_requests : [];
  return proposals.length ? { proposalData: proposals } : {};
};

const fromApiLead = (raw: ApiProspeccaoLead): ProspeccaoLead => ({
  id: String(raw.id || makeId()),
  company: String(raw.nome_empresa || '').trim(),
  contactName: String(raw.responsavel_empresa || '').trim(),
  phone: String(raw.contacto || '').trim(),
  email: String(raw.email || '').trim(),
  googleMapsLink: String(raw.google_maps_link || '').trim(),
  produto: normalizeProspeccaoProduto(raw.produto),
  substage: isValidSubstage(raw.ccc_substage)
    ? normalizeProspeccaoSubstage(raw.ccc_substage)
    : PROSPECCAO_DEFAULT_SUBSTAGE,
  createdAt: String(raw.created_at || new Date().toISOString()),
  updatedAt: String(raw.updated_at || new Date().toISOString()),
  createdBy: raw.created_by ? Number(raw.created_by) : null,
  assignedTo: raw.assigned_to ? Number(raw.assigned_to) : null,
  tentativasContacto: raw.tentativas_contacto ?? null,
  semInteresseMotivo: raw.sem_interesse_motivo ?? null,
  pendenteMotivo: raw.pendente_motivo ?? null,
  fidelizadosDados: raw.fidelizados_dados ?? null,
  envioProposta: raw.envio_proposta ?? null,
  assinadoDados: raw.assinado_dados ?? null,
  proposalRequests: Array.isArray(raw.proposal_requests) ? raw.proposal_requests : [],
  marcacaoFormData: marcacaoFormDataFromApiLead(raw),
  marcacaoId: isValidMarcacaoId(raw.marcacao_id)
    ? String(raw.marcacao_id).trim()
    : resolveProspeccaoMarcacaoId(String(raw.id || makeId())),
});

const defaultLeadInfoTab = (substage: string): LeadInfoTab => {
  const normalized = normalizeProspeccaoSubstage(substage);
  if (isAssinadoSubstage(normalized)) return 'assinado';
  if (isEnvioPropostaSubstage(normalized)) return 'envio_proposta';
  if (isProspeccaoNaoAtendidoSubstage(normalized)) return 'tentativas_contacto';
  if (isFidelizadosSubstage(normalized)) return 'fidelizados';
  if (isMotivoTabSubstage(normalized)) return 'motivo';
  return 'dados';
};

const fmt = (iso?: string) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const LEADS_PER_PAGE = 10;

const sortLeadsByNewest = (a: ProspeccaoLead, b: ProspeccaoLead) => {
  const ta = parseCreatedAt(a.createdAt)?.getTime() ?? 0;
  const tb = parseCreatedAt(b.createdAt)?.getTime() ?? 0;
  return tb - ta;
};

const leadMatchesCreationDate = (lead: ProspeccaoLead, dateFrom: string) =>
  matchesCreationDateFrom(lead.createdAt, dateFrom);

const buildProspeccaoCalendarClient = (lead: ProspeccaoLead): CccCalendarClientOption => ({
  client_type: 'prospeccao',
  client_ref: lead.id,
  label: lead.company || `Lead ${lead.id}`,
  subtitle: [lead.contactName, lead.phone].filter(Boolean).join(' · '),
  route_path: '/prospeccao',
  route_query: { ref: lead.id },
  snapshot: {
    lead_id: lead.id,
    nome_empresa: lead.company,
    nome_responsavel: lead.contactName,
    contacto: lead.phone,
    email: lead.email,
  },
});

const canAccessHistoricoTab = (user?: Record<string, unknown> | null) => {
  const role = String(user?.role || '').trim().toLowerCase();
  const tenant = String(user?.tenant || '').trim().toLowerCase();
  const businessArea = String(user?.business_area || '').trim().toLowerCase();
  if (role.includes('admin') || tenant === 'admin' || businessArea === 'admin') return true;
  return role.includes('gerente') || role.includes('manager');
};

const buildProspeccaoInvestigateQuickSearchSeed = (
  lead: ProspeccaoLead
): RecuperacaoInvestigateQuickSearchSeed => {
  const empresa = lead.company.trim();
  const contacto = lead.phone.trim();
  const responsavel = lead.contactName.trim();
  return {
    nome_empresa: empresa,
    contacto_cliente: contacto,
    ...(responsavel && responsavel !== empresa ? { nome_responsavel: responsavel } : {}),
  };
};

const ProspeccaoBoard: React.FC = () => {
  const { user } = useCccAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [leads, setLeads] = useState<ProspeccaoLead[]>([]);
  const [activeSection, setActiveSection] = useState<ProspeccaoSectionKey>('por_contactar');
  const [expandedSubsections, setExpandedSubsections] = useState<Record<string, boolean>>({});
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [leadInfoTab, setLeadInfoTab] = useState<LeadInfoTab>('dados');
  const [showSolicitarProposta, setShowSolicitarProposta] = useState(false);
  const [solicitarPropostaLead, setSolicitarPropostaLead] = useState<ProspeccaoLead | null>(null);
  const [preparingProposta, setPreparingProposta] = useState(false);
  const [showRefazerProposta, setShowRefazerProposta] = useState(false);
  const [refazerPropostaLead, setRefazerPropostaLead] = useState<ProspeccaoLead | null>(null);
  const [preparingRefazer, setPreparingRefazer] = useState(false);
  const [showSubmeterContrato, setShowSubmeterContrato] = useState(false);
  const [submeterContratoLead, setSubmeterContratoLead] = useState<ProspeccaoLead | null>(null);
  const [preparingContrato, setPreparingContrato] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [expandedPedidoServicos, setExpandedPedidoServicos] = useState<Record<string, boolean>>(
    {}
  );
  const [search, setSearch] = useState('');
  const [creationDateFrom, setCreationDateFrom] = useState('');
  const [workspaceFocus, setWorkspaceFocus] = useState<{
    leadId: string;
    substage: string;
    infoTab?: 'historico';
  } | null>(null);
  const urlRefHandledRef = useRef<string | null>(null);
  const skipDefaultLeadTabRef = useRef(false);
  const leadTabInitializedRef = useRef<string | null>(null);
  const [subsectionPages, setSubsectionPages] = useState<Record<string, number>>({});
  const [investigateOpen, setInvestigateOpen] = useState(false);
  const [investigateQuickSearch, setInvestigateQuickSearch] =
    useState<RecuperacaoInvestigateQuickSearchSeed | null>(null);
  const [duplicateFlags, setDuplicateFlags] = useState<Record<string, CccWorkspaceDuplicateFlags>>({});
  const [showCreate, setShowCreate] = useState(false);
  const [gestaoModal, setGestaoModal] = useState<CccGestaoModalKind | null>(null);
  const [gestaoModalNonce, setGestaoModalNonce] = useState(0);
  const [showPedirAssinaturaDigital, setShowPedirAssinaturaDigital] = useState(false);
  const [pedirAssinaturaNonce, setPedirAssinaturaNonce] = useState(0);
  const [draft, setDraft] = useState<LeadDraft>(emptyDraft);
  const [loading, setLoading] = useState(false);
  const [leadNotes, setLeadNotes] = useState<Record<string, LeadNote[]>>({});
  const [leadNoteDrafts, setLeadNoteDrafts] = useState<Record<string, string>>({});
  const [savingLeadNoteId, setSavingLeadNoteId] = useState<string | null>(null);
  const [editingLeadNoteKey, setEditingLeadNoteKey] = useState<string | null>(null);
  const [editingLeadNoteText, setEditingLeadNoteText] = useState('');
  const [mutatingLeadNoteKey, setMutatingLeadNoteKey] = useState<string | null>(null);
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [calendarLead, setCalendarLead] = useState<ProspeccaoLead | null>(null);
  const [calendarSlot, setCalendarSlot] = useState(() => defaultCalendarSlotTimes());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarSearchTerm, setSidebarSearchTerm] = useState('');
  const [workspaceUsers, setWorkspaceUsers] = useState<WorkspaceUserOption[]>([]);
  const [workspaceUserId, setWorkspaceUserId] = useState<number | 'all' | null>(null);
  const [bulkSelectedIds, setBulkSelectedIds] = useState<Set<string>>(() => new Set());
  const [alterarComercialOpen, setAlterarComercialOpen] = useState(false);
  const [alterarComercialSubmitting, setAlterarComercialSubmitting] = useState(false);
  const canViewHistory = useMemo(() => canAccessHistoricoTab(user), [user]);
  const [isMobileOrTablet] = useState(() => {
    if (typeof window === 'undefined') return false;
    const hasTouch =
      (typeof navigator !== 'undefined' && (navigator.maxTouchPoints || 0) > 0) ||
      'ontouchstart' in window;
    const isTabletWidth = window.innerWidth <= 1100;
    const isSmallScreen = window.matchMedia('(max-width: 768px)').matches;
    return hasTouch && (isTabletWidth || isSmallScreen);
  });

  const canManageWorkspaceScope = useMemo(() => isCccCoordenadorOrAdmin(user), [user]);

  const canReassignComercial = useMemo(() => {
    if (isPlatformAdmin(user)) return true;
    const role = String(user?.role || '').trim().toLowerCase();
    const tenant = String(user?.tenant || '').trim().toLowerCase();
    const businessArea = String(user?.business_area || '').trim().toLowerCase();
    if (role.includes('admin') || tenant === 'admin' || businessArea === 'admin') return true;
    return role.includes('gerente') || role.includes('manager');
  }, [user]);

  const currentUserId = useMemo(() => {
    const raw = user?.id;
    if (raw === null || raw === undefined || raw === '') return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }, [user]);

  const loadDuplicateFlags = useCallback(async () => {
    try {
      const res = await cccApi.getCccWorkspaceDuplicateFlags();
      if (res.success) {
        setDuplicateFlags(res.prospeccao || {});
      }
    } catch {
      /* non-blocking */
    }
  }, []);

  const loadLeads = async () => {
    setLoading(true);
    try {
      const res = await cccApi.getProspeccaoLeads();
      if (!res.success) {
        message.error(res.message || 'Não foi possível carregar os leads.');
        setLeads([]);
        return;
      }
      setLeads((res.leads || []).map(fromApiLead));
      setLeadNotes(buildLeadNotesMap(res.leads || []));
      void loadDuplicateFlags();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao carregar leads de prospeção';
      message.error(msg);
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadLeads();
  }, []);

  useEffect(() => {
    if (!canManageWorkspaceScope) {
      setWorkspaceUserId(currentUserId);
      return;
    }
    if (workspaceUserId === null) setWorkspaceUserId('all');
  }, [canManageWorkspaceScope, currentUserId, workspaceUserId]);

  useEffect(() => {
    if (!canManageWorkspaceScope) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await cccApi.getCccDashboardUsers();
        if (cancelled || !res.success) return;
        const options = (res.users || [])
          .map((u) => ({
            id: Number(u.id),
            name: String(u.name || u.email || `Utilizador ${u.id}`),
            username: undefined,
            email: u.email ? String(u.email) : undefined,
          }))
          .filter((u: WorkspaceUserOption) => Number.isFinite(u.id))
          .sort((a, b) => a.name.localeCompare(b.name, 'pt'));
        setWorkspaceUsers(options);
      } catch {
        if (!cancelled) setWorkspaceUsers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canManageWorkspaceScope]);

  const filtered = useMemo(() => {
    const scoped = leads.filter((lead) => {
      if (canManageWorkspaceScope) {
        if (workspaceUserId === 'all' || workspaceUserId === null) return true;
        const ownerId = lead.assignedTo ?? lead.createdBy;
        return ownerId === workspaceUserId;
      }
      if (!currentUserId) return true;
      const ownerId = lead.assignedTo ?? lead.createdBy;
      return ownerId === currentUserId;
    });
    return scoped.filter((lead) => leadMatchesCreationDate(lead, creationDateFrom));
  }, [leads, creationDateFrom, canManageWorkspaceScope, workspaceUserId, currentUserId]);

  useEffect(() => {
    setSubsectionPages({});
  }, [creationDateFrom, workspaceUserId, activeSection]);

  const applyCreationDatePreset = (key: CreationDatePresetKey) => {
    setCreationDateFrom(presetCutoffPickerValue(key));
  };

  const gestaoFilterUserId = useMemo(() => {
    if (!canManageWorkspaceScope) return currentUserId;
    if (workspaceUserId === 'all' || workspaceUserId === null) return currentUserId;
    return workspaceUserId;
  }, [canManageWorkspaceScope, currentUserId, workspaceUserId]);

  const workspaceSelectionLabel = useMemo(() => {
    if (!canManageWorkspaceScope) return '';
    if (workspaceUserId === 'all' || workspaceUserId === null) return 'Toda a Equipa';
    const match = workspaceUsers.find((u) => u.id === workspaceUserId);
    return match ? match.name : 'Utilizador selecionado';
  }, [canManageWorkspaceScope, workspaceUserId, workspaceUsers]);

  const filteredSidebarUsers = useMemo(() => {
    if (!sidebarSearchTerm.trim()) return workspaceUsers;
    const searchLower = sidebarSearchTerm.toLowerCase();
    return workspaceUsers.filter((u) => {
      const name = (u.name || u.username || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      return name.includes(searchLower) || email.includes(searchLower);
    });
  }, [workspaceUsers, sidebarSearchTerm]);

  const bySubstage = useMemo(() => {
    const map: Record<string, ProspeccaoLead[]> = {};
    PROSPECCAO_SECTIONS.forEach((section) => {
      section.subsections.forEach((sub) => {
        map[sub.substage] = [];
      });
    });
    filtered.forEach((lead) => {
      const normalized = normalizeProspeccaoSubstage(lead.substage);
      if (map[normalized]) map[normalized].push(lead);
    });
    Object.keys(map).forEach((key) => {
      map[key].sort(sortLeadsByNewest);
    });
    return map;
  }, [filtered]);

  const setSubsectionPage = (substage: string, page: number) => {
    setSubsectionPages((prev) => ({ ...prev, [substage]: Math.max(1, page) }));
  };

  const sectionCounts = useMemo(() => {
    const counts: Record<ProspeccaoSectionKey, number> = {
      por_contactar: 0,
      em_tratamento: 0,
      pre_fecho: 0,
      fecho: 0,
    };
    PROSPECCAO_SECTIONS.forEach((section) => {
      counts[section.key] = section.subsections.reduce(
        (total, sub) => total + (bySubstage[sub.substage]?.length || 0),
        0
      );
    });
    return counts;
  }, [bySubstage]);

  const activeConfig = PROSPECCAO_SECTIONS.find((section) => section.key === activeSection)!;
  const selectedLead = leads.find((lead) => lead.id === selectedLeadId) || null;
  const selectedLeadSubstage = normalizeProspeccaoSubstage(
    selectedLead?.substage || PROSPECCAO_DEFAULT_SUBSTAGE
  );
  const motivoVariant: WorkspaceMotivoVariant | null =
    getMotivoVariantForSubstage(selectedLeadSubstage);
  const showMotivoTab = isMotivoTabSubstage(selectedLeadSubstage);
  const showFidelizadosTab = isFidelizadosSubstage(selectedLeadSubstage);
  const showEnvioPropostaTab = isEnvioPropostaSubstage(selectedLeadSubstage);
  const showAssinadoTab = isAssinadoSubstage(selectedLeadSubstage);
  const showTentativasTab = isProspeccaoNaoAtendidoSubstage(selectedLeadSubstage);
  const showWorkspaceFormTab =
    showMotivoTab || showFidelizadosTab || showTentativasTab || showEnvioPropostaTab || showAssinadoTab;
  const motivoData =
    motivoVariant === 'pendente'
      ? selectedLead?.pendenteMotivo
      : motivoVariant === 'sem_interesse'
        ? selectedLead?.semInteresseMotivo
        : null;
  const motivoIncomplete = isWorkspaceMotivoIncomplete(motivoData);
  const fidelizadosIncomplete = isFidelizadosDadosIncomplete(selectedLead?.fidelizadosDados);
  const tentativasIncomplete = isTentativasContactoIncomplete(
    selectedLead?.tentativasContacto
  );
  const workspaceFormIncomplete =
    motivoIncomplete || fidelizadosIncomplete || tentativasIncomplete;
  const workspaceFormWarnTitle = showTentativasTab
    ? TENTATIVAS_CONTACTO_WARNING_TEXT
    : showFidelizadosTab
      ? FIDELIZADOS_WARNING_TEXT
      : motivoVariant === 'pendente'
        ? PENDENTE_MOTIVO_WARNING_TEXT
        : motivoVariant === 'sem_interesse'
          ? SEM_INTERESSE_MOTIVO_WARNING_TEXT
          : MOTIVO_WARNING_TEXT;
  const selectedPedidoExists = useMemo(
    () => hasCccPedidoProposta(selectedLead?.marcacaoFormData),
    [selectedLead]
  );
  const selectedPedidoLayout = useMemo(() => {
    if (!selectedLead) {
      return { empresa: null, servicos: [], comercialRequests: [] };
    }
    return buildPedidoPropostaLeadDetailsLayout({
      id: Number(selectedLead.id) || 0,
      marcacao_id: selectedLead.marcacaoId || selectedLead.id,
      nome_empresa: selectedLead.company,
      nome_responsavel: selectedLead.contactName,
      morada: selectedLead.googleMapsLink || '',
      contacto_cliente: selectedLead.phone,
      form_data: selectedLead.marcacaoFormData,
    } as RecuperacaoMarcacao);
  }, [selectedLead]);

  useEffect(() => {
    if (!selectedLeadId) {
      leadTabInitializedRef.current = null;
      return;
    }
    if (leadTabInitializedRef.current === selectedLeadId) return;
    const lead = leads.find((row) => row.id === selectedLeadId);
    if (!lead) return;
    if (skipDefaultLeadTabRef.current) {
      skipDefaultLeadTabRef.current = false;
      leadTabInitializedRef.current = selectedLeadId;
      if (canViewHistory) {
        setLeadInfoTab('historico');
        return;
      }
    }
    leadTabInitializedRef.current = selectedLeadId;
    setLeadInfoTab(defaultLeadInfoTab(lead.substage));
  }, [selectedLeadId, leads, canViewHistory]);

  useEffect(() => {
    if (!showAssinadoTab && leadInfoTab === 'assinado') {
      setLeadInfoTab(defaultLeadInfoTab(selectedLeadSubstage));
    }
    if (!showEnvioPropostaTab && leadInfoTab === 'envio_proposta') {
      setLeadInfoTab(defaultLeadInfoTab(selectedLeadSubstage));
    }
    if (!showTentativasTab && leadInfoTab === 'tentativas_contacto') {
      setLeadInfoTab(defaultLeadInfoTab(selectedLeadSubstage));
    }
    if (!showFidelizadosTab && leadInfoTab === 'fidelizados') {
      setLeadInfoTab(defaultLeadInfoTab(selectedLeadSubstage));
    }
    if (!showMotivoTab && leadInfoTab === 'motivo') {
      setLeadInfoTab(defaultLeadInfoTab(selectedLeadSubstage));
    }
  }, [
    showAssinadoTab,
    showEnvioPropostaTab,
    showTentativasTab,
    showFidelizadosTab,
    showMotivoTab,
    leadInfoTab,
    selectedLeadSubstage,
  ]);

  useEffect(() => {
    if (leadInfoTab === 'pedido' && !selectedPedidoExists) {
      setLeadInfoTab('dados');
    }
  }, [leadInfoTab, selectedPedidoExists]);

  useEffect(() => {
    if (leadInfoTab !== 'pedido' || !selectedLeadId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await cccApi.getProspeccaoLeadMarcacao(selectedLeadId);
        if (cancelled || !res.success) return;
        const marcacao = res.marcacao as { form_data?: Record<string, unknown> } | undefined;
        const formData =
          marcacao?.form_data && typeof marcacao.form_data === 'object'
            ? marcacao.form_data
            : null;
        if (!formData) return;
        const proposalData = Array.isArray(formData.proposalData) ? formData.proposalData : [];
        setLeads((prev) =>
          prev.map((row) =>
            row.id === selectedLeadId
              ? {
                  ...row,
                  marcacaoFormData: formData,
                  proposalRequests: proposalData as Record<string, unknown>[],
                }
              : row
          )
        );
      } catch {
        /* best-effort refresh for BackOffice completion */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadInfoTab, selectedLeadId, historyRefreshKey]);

  const handleTentativasSaved = useCallback((raw: ApiProspeccaoLead) => {
    const normalized = fromApiLead(raw);
    setLeads((prev) => prev.map((row) => (row.id === normalized.id ? normalized : row)));
  }, []);

  const handleLeadFieldSaved = useCallback((raw: ApiProspeccaoLead) => {
    const normalized = fromApiLead(raw);
    setLeads((prev) => prev.map((row) => (row.id === normalized.id ? normalized : row)));
  }, []);

  useEffect(() => {
    const ref = searchParams.get('ref')?.trim();
    if (!ref) {
      urlRefHandledRef.current = null;
      return;
    }
    if (loading) return;
    if (urlRefHandledRef.current === ref) return;
    const lead = leads.find((row) => row.id === ref);
    if (!lead) return;
    urlRefHandledRef.current = ref;
    const tab = searchParams.get('tab')?.trim();
    setWorkspaceFocus({
      leadId: ref,
      substage: normalizeProspeccaoSubstage(lead.substage),
      ...(tab === 'historico' ? { infoTab: 'historico' as const } : {}),
    });
  }, [searchParams, leads, loading]);

  useEffect(() => {
    if (!workspaceFocus) return;
    const { leadId, substage, infoTab } = workspaceFocus;
    const normalized = normalizeProspeccaoSubstage(substage);
    const section = prospeccaoSectionForSubstage(normalized);
    if (section) setActiveSection(section.key);
    setExpandedSubsections((prev) => ({ ...prev, [normalized]: true }));
    const rows = bySubstage[normalized] || [];
    const idx = rows.findIndex((lead) => lead.id === leadId);
    if (idx >= 0) {
      setSubsectionPages((prev) => ({ ...prev, [normalized]: Math.floor(idx / LEADS_PER_PAGE) + 1 }));
    }
    if (infoTab === 'historico' && canViewHistory) {
      skipDefaultLeadTabRef.current = true;
    }
    setSelectedLeadId(leadId);
    const timer = window.setTimeout(() => setWorkspaceFocus(null), 0);
    return () => window.clearTimeout(timer);
  }, [workspaceFocus, bySubstage, canViewHistory]);

  const createLead = async () => {
    const company = draft.company.trim();
    const contactName = draft.contactName.trim();
    const phone = draft.phone.trim();
    const email = draft.email.trim();
    if (!company || !contactName || !phone) {
      message.error('Nome da empresa, responsável e contacto são obrigatórios.');
      return;
    }
    try {
      const res = await cccApi.createProspeccaoLead({
        nome_empresa: company,
        responsavel_empresa: contactName,
        contacto: phone,
        email: email || null,
        google_maps_link: draft.googleMapsLink.trim() || null,
        produto: draft.produto.length ? draft.produto : null,
        ccc_substage: PROSPECCAO_DEFAULT_SUBSTAGE,
      });
      if (!res.success || !res.lead) {
        message.error(res.message || 'Não foi possível criar a lead.');
        return;
      }
      const lead = fromApiLead(res.lead);
      setLeads((prev) => [lead, ...prev]);
      setSelectedLeadId(lead.id);
      setExpandedSubsections((prev) => ({ ...prev, [PROSPECCAO_DEFAULT_SUBSTAGE]: true }));
      setActiveSection('por_contactar');
      setDraft(emptyDraft);
      setShowCreate(false);
      message.success('Lead criada na Prospeção.');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao criar lead';
      message.error(msg);
    }
  };

  const patchLead = (id: string, patch: Partial<ProspeccaoLead>) => {
    setLeads((prev) =>
      prev.map((lead) =>
        lead.id === id
          ? {
              ...lead,
              ...patch,
              updatedAt: new Date().toISOString(),
            }
          : lead
      )
    );
  };

  const saveLead = async (lead: ProspeccaoLead) => {
    try {
      const res = await cccApi.updateProspeccaoLead(lead.id, {
        nome_empresa: lead.company,
        responsavel_empresa: lead.contactName,
        contacto: lead.phone,
        email: lead.email.trim() || null,
        google_maps_link: lead.googleMapsLink || null,
        produto: lead.produto.length ? lead.produto : null,
      });
      if (!res.success || !res.lead) {
        message.error(res.message || 'Não foi possível guardar a lead.');
        return;
      }
      const normalized = fromApiLead(res.lead);
      setLeads((prev) => prev.map((row) => (row.id === normalized.id ? normalized : row)));
      message.success('Lead guardada.');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao guardar lead';
      message.error(msg);
    }
  };

  const changeLeadSubstage = async (leadId: string, substage: string) => {
    const lead = leads.find((row) => row.id === leadId);
    if (
      lead &&
      isProspeccaoNaoAtendidoSubstage(normalizeProspeccaoSubstage(lead.substage)) &&
      !isProspeccaoNaoAtendidoSubstage(normalizeProspeccaoSubstage(substage)) &&
      isTentativasContactoIncomplete(lead.tentativasContacto)
    ) {
      message.warning(TENTATIVAS_CONTACTO_WARNING_TEXT);
      setLeadInfoTab('tentativas_contacto');
      return;
    }
    try {
      const res = await cccApi.updateProspeccaoLeadSubstage(leadId, substage);
      if (!res.success || !res.lead) {
        message.error(res.message || 'Não foi possível atualizar o sub-estágio.');
        return;
      }
      const normalized = fromApiLead(res.lead);
      setLeads((prev) => prev.map((row) => (row.id === normalized.id ? normalized : row)));
      const section = PROSPECCAO_SECTIONS.find((s) => s.subsections.some((sub) => sub.substage === substage));
      if (section) setActiveSection(section.key);
      setExpandedSubsections((prev) => ({ ...prev, [substage]: true }));
      if (selectedLeadId === leadId) {
        setLeadInfoTab(defaultLeadInfoTab(normalized.substage));
      }
      setHistoryRefreshKey((k) => k + 1);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao atualizar sub-estágio';
      message.error(msg);
    }
  };

  const isLeadMine = (lead: ProspeccaoLead) => {
    if (!currentUserId) return false;
    const ownerId = lead.assignedTo ?? lead.createdBy;
    return ownerId === currentUserId;
  };

  const handleSolicitarProposta = async (lead: ProspeccaoLead) => {
    if (!isLeadMine(lead)) {
      message.warning(
        'A lead tem de estar atribuída a si antes de solicitar proposta.'
      );
      return;
    }
    setPreparingProposta(true);
    try {
      const res = await cccApi.prepareSolicitarPropostaProspeccaoLead(lead.id);
      if (!res.success) {
        message.error(
          res.message || 'Não foi possível atualizar o estado para "A aguardar Proposta BO".'
        );
        return;
      }
      const marcacaoId = resolveProspeccaoMarcacaoId(lead.id, res.marcacao_id);
      if (!isValidMarcacaoId(marcacaoId)) {
        message.error('Não foi possível obter o ID da marcação para Solicitar Proposta.');
        return;
      }
      if (res.lead) {
        const normalized = fromApiLead(res.lead);
        normalized.marcacaoId = marcacaoId;
        setLeads((prev) => prev.map((row) => (row.id === normalized.id ? normalized : row)));
        setSolicitarPropostaLead(normalized);
      } else {
        setSolicitarPropostaLead({
          ...lead,
          substage: PROSPECCAO_SUBSTAGE_AGUARDAR_PROPOSTA_BO,
          marcacaoId,
        });
      }
      const section = prospeccaoSectionForSubstage(PROSPECCAO_SUBSTAGE_AGUARDAR_PROPOSTA_BO);
      if (section) setActiveSection(section.key);
      setExpandedSubsections((prev) => ({
        ...prev,
        [PROSPECCAO_SUBSTAGE_AGUARDAR_PROPOSTA_BO]: true,
      }));
      setHistoryRefreshKey((k) => k + 1);
      setShowSolicitarProposta(true);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao atualizar estado antes de abrir Solicitar Proposta';
      message.error(msg);
    } finally {
      setPreparingProposta(false);
    }
  };

  const handleRefazerProposta = async (lead: ProspeccaoLead) => {
    setPreparingRefazer(true);
    try {
      const data = await cccApi.getProspeccaoLeadMarcacao(lead.id);
      if (!data?.success || !data.marcacao) {
        message.error('Não foi possível carregar a marcação para Refazer.');
        return;
      }
      const marcacaoId = resolveProspeccaoMarcacaoId(lead.id, data.marcacao_id);
      if (!isValidMarcacaoId(marcacaoId)) {
        message.error('Não foi possível obter o ID da marcação para Refazer.');
        return;
      }
      const completed = buildCompletedProposalsForMarcacao(
        data.marcacao as Parameters<typeof buildCompletedProposalsForMarcacao>[0]
      );
      if (completed.length === 0) {
        message.info('Não existem pedidos concluídos para refazer nesta marcação.');
        return;
      }
      setRefazerPropostaLead({
        ...lead,
        marcacaoId,
        marcacaoFormData:
          (data.marcacao.form_data as Record<string, unknown> | undefined) || lead.marcacaoFormData,
      });
      setShowRefazerProposta(true);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao abrir Refazer';
      message.error(msg);
    } finally {
      setPreparingRefazer(false);
    }
  };

  const handleSubmeterContrato = async (lead: ProspeccaoLead) => {
    if (!isLeadMine(lead)) {
      message.warning('A lead tem de estar atribuída a si antes de submeter contrato.');
      return;
    }
    setPreparingContrato(true);
    try {
      const res = await cccApi.getProspeccaoLeadMarcacao(lead.id);
      if (!res.success) {
        message.error(res.message || 'Não foi possível carregar a marcação para submeter contrato.');
        return;
      }
      const marcacaoId = resolveProspeccaoMarcacaoId(lead.id, res.marcacao_id);
      if (!isValidMarcacaoId(marcacaoId)) {
        message.error('Não foi possível obter o ID da marcação para Submeter Contrato.');
        return;
      }
      if (res.marcacao) {
        const formData = (res.marcacao as { form_data?: Record<string, unknown> }).form_data;
        if (formData && typeof formData === 'object') {
          const proposalData = Array.isArray(formData.proposalData) ? formData.proposalData : [];
          setLeads((prev) =>
            prev.map((row) =>
              row.id === lead.id
                ? {
                    ...row,
                    marcacaoId,
                    marcacaoFormData: formData,
                    proposalRequests: proposalData as Record<string, unknown>[],
                  }
                : row
            )
          );
        }
      }
      setSubmeterContratoLead({ ...lead, marcacaoId });
      setShowSubmeterContrato(true);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao abrir Submeter Contrato';
      message.error(msg);
    } finally {
      setPreparingContrato(false);
    }
  };

  const handleContractSuccess = async () => {
    setShowSubmeterContrato(false);
    const leadId = submeterContratoLead?.id;
    setSubmeterContratoLead(null);
    if (leadId) {
      try {
        const marcRes = await cccApi.getProspeccaoLeadMarcacao(leadId);
        if (marcRes.success && marcRes.marcacao) {
          const formData = (marcRes.marcacao as { form_data?: Record<string, unknown> }).form_data;
          if (formData && typeof formData === 'object') {
            setLeads((prev) =>
              prev.map((row) =>
                row.id === leadId ? { ...row, marcacaoFormData: formData } : row
              )
            );
          }
        }
      } catch {
        /* refresh best-effort */
      }
    }
    setHistoryRefreshKey((k) => k + 1);
    message.success('Contrato submetido com sucesso.');
  };

  const handleProposalSuccess = async () => {
    setShowSolicitarProposta(false);
    setShowRefazerProposta(false);
    const leadId = solicitarPropostaLead?.id || refazerPropostaLead?.id;
    setSolicitarPropostaLead(null);
    setRefazerPropostaLead(null);
    if (leadId) {
      try {
        const res = await cccApi.getProspeccaoLeads();
        if (res.success && Array.isArray(res.leads)) {
          const updated = res.leads.find((l) => String(l.id) === leadId);
          if (updated) {
            const normalized = fromApiLead(updated);
            setLeads((prev) => prev.map((row) => (row.id === normalized.id ? normalized : row)));
            setLeadNotes((prev) => ({
              ...prev,
              [leadId]: notesFromApiLead(updated),
            }));
          }
        }
        const marcRes = await cccApi.getProspeccaoLeadMarcacao(leadId);
        if (marcRes.success && marcRes.marcacao) {
          const formData = (marcRes.marcacao as { form_data?: Record<string, unknown> }).form_data;
          if (formData && typeof formData === 'object') {
            const proposalData = Array.isArray(formData.proposalData) ? formData.proposalData : [];
            setLeads((prev) =>
              prev.map((row) =>
                row.id === leadId
                  ? {
                      ...row,
                      marcacaoFormData: formData,
                      proposalRequests: proposalData as Record<string, unknown>[],
                    }
                  : row
              )
            );
          }
        }
      } catch {
        /* refresh best-effort */
      }
    }
    setHistoryRefreshKey((k) => k + 1);
    setLeadInfoTab('pedido');
    message.success(
      'Pedido de proposta submetido. Lead movida para "A aguardar Proposta BO".'
    );
  };

  const removeLead = async (id: string) => {
    try {
      const res = await cccApi.deleteProspeccaoLead(id);
      if (!res.success) {
        message.error(res.message || 'Não foi possível remover a lead.');
        return;
      }
      setLeads((prev) => prev.filter((lead) => lead.id !== id));
      setLeadNotes((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setLeadNoteDrafts((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      if (selectedLeadId === id) setSelectedLeadId(null);
      message.success('Lead removida da lista.');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao remover lead';
      message.error(msg);
    }
  };

  const confirmRemoveLead = (lead: ProspeccaoLead) => {
    Modal.confirm({
      className: 'prosp-confirm-modal',
      centered: true,
      icon: <ExclamationCircleOutlined />,
      title: 'Remover lead?',
      content: `A lead "${lead.company || 'Sem nome'}" será removida da lista de Prospeção.`,
      okText: 'Remover',
      cancelText: 'Cancelar',
      okType: 'danger',
      okButtonProps: { className: 'prosp-confirm-ok' },
      cancelButtonProps: { className: 'prosp-confirm-cancel' },
      onOk: () => removeLead(lead.id),
    });
  };

  const saveLeadNote = async (leadId: string) => {
    const text = (leadNoteDrafts[leadId] || '').trim();
    if (!text || savingLeadNoteId === leadId) return;
    setSavingLeadNoteId(leadId);
    try {
      const res = await cccApi.addProspeccaoLeadNote(leadId, text);
      if (!res.success || !res.lead) {
        message.error(res.message || 'Não foi possível guardar a nota.');
        return;
      }
      setLeadNotes((prev) => ({
        ...prev,
        [leadId]: notesFromApiLead(res.lead as ApiProspeccaoLead),
      }));
      setLeadNoteDrafts((prev) => ({ ...prev, [leadId]: '' }));
      message.success('Nota guardada.');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao guardar nota';
      message.error(msg);
    } finally {
      setSavingLeadNoteId(null);
    }
  };

  const leadNoteKey = (leadId: string, noteId: string) => `${leadId}:${noteId}`;

  const canModifyLeadNote = useCallback(
    (note: LeadNote) => {
      if (canManageWorkspaceScope) return true;
      if (currentUserId == null || note.by_id == null) return false;
      return Number(note.by_id) === Number(currentUserId);
    },
    [canManageWorkspaceScope, currentUserId]
  );

  const applyLeadNotesFromResponse = (leadId: string, lead: ApiProspeccaoLead) => {
    setLeadNotes((prev) => ({
      ...prev,
      [leadId]: notesFromApiLead(lead),
    }));
  };

  const startEditLeadNote = (leadId: string, note: LeadNote) => {
    if (!note.id) return;
    setEditingLeadNoteKey(leadNoteKey(leadId, note.id));
    setEditingLeadNoteText(note.text);
  };

  const cancelEditLeadNote = () => {
    setEditingLeadNoteKey(null);
    setEditingLeadNoteText('');
  };

  const saveEditLeadNote = async (leadId: string, noteId: string) => {
    const text = editingLeadNoteText.trim();
    const key = leadNoteKey(leadId, noteId);
    if (!text || mutatingLeadNoteKey === key) return;
    setMutatingLeadNoteKey(key);
    try {
      const res = await cccApi.updateProspeccaoLeadNote(leadId, noteId, text);
      if (!res.success || !res.lead) {
        message.error(res.message || 'Não foi possível atualizar a nota.');
        return;
      }
      applyLeadNotesFromResponse(leadId, res.lead as ApiProspeccaoLead);
      cancelEditLeadNote();
      message.success('Nota atualizada.');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao atualizar nota';
      message.error(msg);
    } finally {
      setMutatingLeadNoteKey(null);
    }
  };

  const deleteLeadNote = async (leadId: string, noteId: string) => {
    const key = leadNoteKey(leadId, noteId);
    if (mutatingLeadNoteKey === key) return;
    setMutatingLeadNoteKey(key);
    try {
      const res = await cccApi.deleteProspeccaoLeadNote(leadId, noteId);
      if (!res.success || !res.lead) {
        message.error(res.message || 'Não foi possível eliminar a nota.');
        return;
      }
      applyLeadNotesFromResponse(leadId, res.lead as ApiProspeccaoLead);
      if (editingLeadNoteKey === key) cancelEditLeadNote();
      message.success('Nota eliminada.');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao eliminar nota';
      message.error(msg);
    } finally {
      setMutatingLeadNoteKey(null);
    }
  };

  const confirmDeleteLeadNote = (leadId: string, note: LeadNote) => {
    if (!note.id) return;
    Modal.confirm({
      className: 'prosp-confirm-modal',
      centered: true,
      icon: <ExclamationCircleOutlined />,
      title: 'Eliminar nota?',
      content: 'Esta ação não pode ser desfeita.',
      okText: 'Eliminar',
      cancelText: 'Cancelar',
      okType: 'danger',
      okButtonProps: { className: 'prosp-confirm-ok' },
      cancelButtonProps: { className: 'prosp-confirm-cancel' },
      onOk: () => deleteLeadNote(leadId, note.id as string),
    });
  };

  const formatLeadNoteMeta = (note: LeadNote) => {
    const author = note.by_name || 'Utilizador';
    const created = fmt(note.at);
    if (note.updated_at && note.updated_by_name) {
      return `${author} · ${created} · editado por ${note.updated_by_name} · ${fmt(note.updated_at)}`;
    }
    return `${author} · ${created}`;
  };

  const openCreateCalendarEvent = (lead: ProspeccaoLead) => {
    setCalendarLead(lead);
    setCalendarSlot(defaultCalendarSlotTimes());
    setCalendarModalOpen(true);
  };

  const handleRefresh = async () => {
    await loadLeads();
    message.success('Lista de leads atualizada.');
  };

  const handleWorkspaceSearch = async () => {
    const q = search.trim();
    if (!q) {
      message.warning('Introduza empresa, contacto ou NIF para pesquisar.');
      return;
    }
    try {
      const matches = await fetchCccWorkspaceSearch('prospeccao', q);
      if (!matches.length) {
        showCccSearchNotFound(q);
        return;
      }
      const match = matches[0];
      const nav = canNavigateToMatch(match, { canManageWorkspaceScope, currentUserId });
      if (!nav.ok) {
        message.warning(nav.reason);
        return;
      }
      setCreationDateFrom('');
      if (canManageWorkspaceScope) {
        setWorkspaceUserId(match.owner_user_id ?? 'all');
      }
      await loadLeads();
      setWorkspaceFocus({
        leadId: match.id,
        substage: normalizeProspeccaoSubstage(match.substage),
      });
      showCccSearchSuccess(match);
      if (matches.length > 1) {
        message.info(`Foram encontrados ${matches.length} resultados. A abrir o primeiro.`);
      }
    } catch (err: unknown) {
      const msg =
        (err as { message?: string })?.message ||
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao pesquisar';
      message.error(msg);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setCreationDateFrom('');
    if (canManageWorkspaceScope) {
      setWorkspaceUserId('all');
    } else if (currentUserId) {
      setWorkspaceUserId(currentUserId);
    }
    setSubsectionPages({});
  };

  const openGestaoModal = (kind: CccGestaoModalKind) => {
    setGestaoModal(kind);
    setGestaoModalNonce((n) => n + 1);
  };

  const openPedirAssinaturaDigital = () => {
    setShowPedirAssinaturaDigital(true);
    setPedirAssinaturaNonce((n) => n + 1);
  };

  const toggleBulkSelect = useCallback((leadId: string, checked: boolean) => {
    setBulkSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(leadId);
      else next.delete(leadId);
      return next;
    });
  }, []);

  const toggleBulkSelectPage = useCallback((leadIds: string[], checked: boolean) => {
    setBulkSelectedIds((prev) => {
      const next = new Set(prev);
      leadIds.forEach((id) => {
        if (checked) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  }, []);

  const clearBulkSelection = useCallback(() => setBulkSelectedIds(new Set()), []);

  const openAlterarComercial = useCallback(() => {
    if (bulkSelectedIds.size === 0) {
      message.warning('Selecione pelo menos um cliente na área de trabalho.');
      return;
    }
    setAlterarComercialOpen(true);
  }, [bulkSelectedIds.size]);

  const handleConfirmAlterarComercial = async (userId: number) => {
    const ids = Array.from(bulkSelectedIds);
    if (!ids.length) return;
    setAlterarComercialSubmitting(true);
    try {
      const res = await cccApi.bulkReassignProspeccaoLeads(ids, userId);
      const count = res.updated_count ?? res.updated?.length ?? 0;
      if (count > 0) {
        message.success(
          count === 1
            ? 'Comercial alterado com sucesso.'
            : `${count} clientes com comercial alterado.`
        );
        clearBulkSelection();
        setAlterarComercialOpen(false);
        await loadLeads();
      } else {
        message.warning('Nenhum cliente foi alterado.');
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Não foi possível alterar o comercial';
      message.error(msg);
    } finally {
      setAlterarComercialSubmitting(false);
    }
  };

  const openInvestigateModal = useCallback((seed?: RecuperacaoInvestigateQuickSearchSeed | null) => {
    setInvestigateQuickSearch(seed ?? null);
    setInvestigateOpen(true);
  }, []);

  const closeInvestigateModal = useCallback(() => {
    setInvestigateOpen(false);
    setInvestigateQuickSearch(null);
  }, []);

  const handleQuickInvestigate = useCallback(
    (seed: RecuperacaoInvestigateQuickSearchSeed) => {
      openInvestigateModal(seed);
    },
    [openInvestigateModal]
  );

  const handleInvestigateOpenMarcacao = async (row: RecuperacaoInvestigateRow) => {
    closeInvestigateModal();

    if (row.workspace === 'prospeccao') {
      const leadId = row.lead_id || String(row.id);
      setWorkspaceFocus({
        leadId,
        substage: normalizeProspeccaoSubstage(row.ccc_substage || PROSPECCAO_DEFAULT_SUBSTAGE),
      });
      message.success(`Lead ${leadId} aberto na área de trabalho.`);
      return;
    }

    const marcacaoId = row.marcacao_id;
    if (!marcacaoId) return;

    if (row.is_available) {
      navigate(`/recuperacao?marcacao=${encodeURIComponent(marcacaoId)}`);
      message.success(`Marcação ${marcacaoId} aberta em Recuperação (Pool).`);
      return;
    }

    navigate(`/recuperacao?marcacao=${encodeURIComponent(marcacaoId)}`);
    message.success(`Marcação ${marcacaoId} aberta em Recuperação.`);
  };

  return (
    <div className="rec-page gestao-marcacoes prosp-page-scope">
      <header className="rec-hero rec-hero--compact">
        <button
          type="button"
          className="rec-investigate-fab"
          onClick={() => openInvestigateModal()}
          aria-label="Investigar marcações"
          title="Investigar Marcações"
        >
          <SearchOutlined />
        </button>
        <div className="rec-hero-top">
          <div className="rec-hero-text">
            <span className="rec-hero-eyebrow">CallCenter Comercial</span>
            <div
              className="gestao-marcacoes-header-title-row prosp-hero-title-row"
              style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}
            >
              {canManageWorkspaceScope && (
                <Button
                  type="text"
                  className="gestao-marcacoes-sidebar-trigger"
                  icon={<TeamOutlined style={{ fontSize: 10 }} />}
                  onClick={() => setDrawerOpen(true)}
                  aria-label="Abrir seleção de utilizador da equipa CCC"
                  style={{
                    backgroundColor: '#ff4d4f',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '3px',
                    padding: 0,
                    width: 20,
                    height: 20,
                    minWidth: 20,
                    minHeight: 20,
                    flexShrink: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                    boxShadow: '0 1px 2px rgba(255, 77, 79, 0.22)',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#ff7875';
                    e.currentTarget.style.boxShadow = '0 2px 5px rgba(255, 77, 79, 0.32)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#ff4d4f';
                    e.currentTarget.style.boxShadow = '0 1px 2px rgba(255, 77, 79, 0.22)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                />
              )}
              <h1 className="rec-hero-title">Prospeção</h1>
              {canManageWorkspaceScope && workspaceSelectionLabel && (
                <div className="management-viewing-user">
                  <div
                    className="user-color management-user-dot"
                    style={{
                      backgroundColor:
                        workspaceUserId === 'all' || workspaceUserId === null
                          ? '#1890ff'
                          : Number(workspaceUserId) % 2 === 0
                            ? '#ff001e'
                            : '#cccccc',
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      flexShrink: 0,
                    }}
                  />
                  <span className="management-user-indicator">{workspaceSelectionLabel}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="rec-toolbar">
          <div className="rec-toolbar-group">
            <input
              type="search"
              className="rec-input"
              placeholder="Pesquisar empresa, contacto ou NIF…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void handleWorkspaceSearch()}
            />
            <button type="button" className="rec-btn rec-btn-ghost" onClick={() => void handleWorkspaceSearch()}>
              Procurar
            </button>
            <div className="prosp-date-filters" role="group" aria-label="Filtrar por data de criação">
              <span className="prosp-date-filters-label">Data de criação</span>
              <div className="prosp-date-filters-buttons">
                {CREATION_DATE_FILTERS.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    className={`prosp-date-filter-btn ${
                      creationDateFrom === presetCutoffPickerValue(option.key) ? 'is-active' : ''
                    }`}
                    onClick={() => applyCreationDatePreset(option.key)}
                    aria-pressed={creationDateFrom === presetCutoffPickerValue(option.key)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <label className="prosp-date-picker-field">
                <span className="prosp-date-filters-label">A partir de</span>
                <input
                  type="date"
                  className={`rec-input prosp-date-picker ${creationDateFrom ? 'is-active' : ''}`}
                  value={creationDateFrom}
                  onChange={(e) => setCreationDateFrom(e.target.value)}
                  aria-label="Filtrar leads criadas a partir desta data"
                />
              </label>
            </div>
          </div>
          <div className="rec-toolbar-group rec-toolbar-group--actions">
            <button type="button" className="rec-btn rec-btn-ghost" onClick={clearFilters}>
              Limpar Filtros
            </button>
            <button type="button" className="rec-btn rec-btn-primary" onClick={() => void handleRefresh()}>
              Atualizar
            </button>
            <button
              type="button"
              className="rec-btn rec-btn-minhas-propostas"
              onClick={() => openGestaoModal('minhas-propostas')}
            >
              Minhas Propostas
            </button>
            <button
              type="button"
              className="rec-btn rec-btn-meus-contratos"
              onClick={() => openGestaoModal('meus-contratos')}
            >
              Meus Contratos
            </button>
            <button
              type="button"
              className="rec-btn rec-btn-pedir-assinatura"
              onClick={openPedirAssinaturaDigital}
            >
              ✒ Pedir Assinatura Digital
            </button>
            <button type="button" className="rec-btn rec-btn-primary" onClick={() => setShowCreate(true)}>
              + Criar Lead
            </button>
            {canReassignComercial && (
              <button
                type="button"
                className="rec-btn rec-btn-ghost rec-btn-alterar-comercial"
                onClick={openAlterarComercial}
              >
                Alterar Comercial
                {bulkSelectedIds.size > 0 ? ` (${bulkSelectedIds.size})` : ''}
              </button>
            )}
          </div>
        </div>
      </header>

      {canReassignComercial && (
        <CccBulkSelectionBar
          selectedCount={bulkSelectedIds.size}
          onClear={clearBulkSelection}
          onAlterarComercial={openAlterarComercial}
        />
      )}

      <div className="rec-ws">
        <nav className="rec-ws-tabs" aria-label="Secções da área de trabalho">
          {PROSPECCAO_SECTIONS.map((section) => (
            <button
              key={section.key}
              type="button"
              className={`rec-ws-tab ${activeSection === section.key ? 'is-active' : ''}`}
              onClick={() => setActiveSection(section.key)}
            >
              <span className="rec-ws-tab-dot" style={{ background: section.accent }} />
              {section.title}
              <span className="rec-ws-tab-count">{sectionCounts[section.key]}</span>
            </button>
          ))}
        </nav>

        <div className="rec-accordions">
          {activeConfig.subsections.map((sub) => {
            const rows = bySubstage[sub.substage] || [];
            const totalRows = rows.length;
            const totalPages = Math.max(1, Math.ceil(totalRows / LEADS_PER_PAGE));
            const requestedPage = subsectionPages[sub.substage] || 1;
            const currentPage = Math.min(Math.max(1, requestedPage), totalPages);
            const pageStart = totalRows === 0 ? 0 : (currentPage - 1) * LEADS_PER_PAGE + 1;
            const pageEnd = Math.min(currentPage * LEADS_PER_PAGE, totalRows);
            const paginatedRows = rows.slice(
              (currentPage - 1) * LEADS_PER_PAGE,
              currentPage * LEADS_PER_PAGE
            );
            const isOpen = expandedSubsections[sub.substage] === true;
            return (
              <section key={sub.substage} className={`rec-accordion ${isOpen ? 'is-open' : ''}`}>
                <button
                  type="button"
                  className="rec-accordion-trigger"
                  onClick={() =>
                    setExpandedSubsections((prev) => ({ ...prev, [sub.substage]: !prev[sub.substage] }))
                  }
                  aria-expanded={isOpen}
                >
                  <span className="rec-accordion-chevron">▼</span>
                  <span>{sub.label}</span>
                  <span className="rec-accordion-count">{totalRows}</span>
                </button>
                {(isOpen || loading) && (
                  <div className="rec-accordion-body">
                    {loading ? (
                      <div className="prosp-accordion-loading">
                        <WonderTradeLoader message="A carregar leads..." />
                      </div>
                    ) : totalRows === 0 ? (
                      <p className="rec-accordion-empty">Sem leads neste estado.</p>
                    ) : (
                      <>
                      <div className="rec-table-scroll">
                        <table className="rec-table rec-table--selectable">
                          <thead>
                            <tr>
                              {canReassignComercial && (
                                <th className="rec-table-col-bulk" scope="col">
                                  <input
                                    type="checkbox"
                                    className="ccc-bulk-checkbox"
                                    aria-label="Selecionar todos nesta página"
                                    checked={
                                      paginatedRows.length > 0 &&
                                      paginatedRows.every((l) => bulkSelectedIds.has(l.id))
                                    }
                                    ref={(el) => {
                                      if (!el) return;
                                      const some = paginatedRows.some((l) => bulkSelectedIds.has(l.id));
                                      const all =
                                        paginatedRows.length > 0 &&
                                        paginatedRows.every((l) => bulkSelectedIds.has(l.id));
                                      el.indeterminate = some && !all;
                                    }}
                                    onChange={(e) =>
                                      toggleBulkSelectPage(
                                        paginatedRows.map((l) => l.id),
                                        e.target.checked
                                      )
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </th>
                              )}
                              <th className="rec-table-col-chevron" aria-hidden />
                              <th>Empresa / Responsável</th>
                              <th>Data e hora de Criação da Lead</th>
                              <th>Contacto / E-mail</th>
                              <th>Estado da Proposta</th>
                              <th className="rec-table-col-actions">Pesquisa</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedRows.map((lead) => {
                              const isSelected = selectedLeadId === lead.id;
                              const isBulkChecked = bulkSelectedIds.has(lead.id);
                              const proposalStatus = resolveLeadProposalStatusDisplay(
                                lead.marcacaoFormData
                              );
                              const dupFlags = duplicateFlags[lead.id];
                              return (
                                <React.Fragment key={lead.id}>
                                  <tr
                                    className={`rec-table-row ${isSelected ? 'is-selected' : ''} ${
                                      isBulkChecked ? 'is-bulk-selected' : ''
                                    }`}
                                    onClick={() => {
                                      setLeadInfoTab('dados');
                                      setSelectedLeadId(isSelected ? null : lead.id);
                                    }}
                                  >
                                    {canReassignComercial && (
                                      <td
                                        className="rec-table-col-bulk"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <input
                                          type="checkbox"
                                          className="ccc-bulk-checkbox"
                                          aria-label={`Selecionar ${lead.company}`}
                                          checked={isBulkChecked}
                                          onChange={(e) => toggleBulkSelect(lead.id, e.target.checked)}
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      </td>
                                    )}
                                    <td className="rec-table-col-chevron">
                                      <span className={`rec-row-chevron ${isSelected ? 'is-open' : ''}`}>▶</span>
                                    </td>
                                    <td className="rec-table-cell-empresa">
                                      <span className="rec-table-empresa-line">
                                        <span className="rec-table-empresa">{lead.company}</span>
                                        <CccWorkspaceDuplicateIndicator flags={dupFlags} />
                                      </span>
                                      <span className="rec-table-responsavel">{lead.contactName}</span>
                                    </td>
                                    <td>
                                      <span className="rec-table-schedule">{fmt(lead.createdAt)}</span>
                                    </td>
                                    <td>
                                      <span className="rec-table-contacto">{lead.phone}</span>
                                      {lead.email && <span className="rec-table-extra">{lead.email}</span>}
                                    </td>
                                    <td>
                                      <span
                                        className={`rec-request-status rec-request-status--${proposalStatus.tone} prosp-estado-proposta-pill`}
                                      >
                                        {proposalStatus.label}
                                      </span>
                                    </td>
                                    <td className="rec-table-col-actions" onClick={(e) => e.stopPropagation()}>
                                      <button
                                        type="button"
                                        className="rec-quick-investigate-btn"
                                        title="Pesquisa rápida — Investigar por empresa e contacto"
                                        aria-label={`Pesquisa rápida: ${lead.company}`}
                                        onClick={() =>
                                          handleQuickInvestigate(buildProspeccaoInvestigateQuickSearchSeed(lead))
                                        }
                                      >
                                        <SearchOutlined />
                                        <span className="rec-quick-investigate-btn__label">Investigar</span>
                                      </button>
                                    </td>
                                  </tr>
                                  {isSelected && (
                                    <tr className="rec-table-detail-row">
                                      <td colSpan={canReassignComercial ? 7 : 6}>
                                        <div className="rec-lead-panel prosp-lead-panel">
                                          <div className="rec-lead-panel-bar">
                                            <div className="rec-lead-panel-bar-title">
                                              <span className="rec-lead-panel-empresa-line">
                                                {showWorkspaceFormTab && workspaceFormIncomplete && (
                                                  <RecuperacaoMotivoWarningIcon
                                                    title={workspaceFormWarnTitle}
                                                  />
                                                )}
                                                <span className="rec-lead-panel-empresa">
                                                  {(selectedLead?.company || lead.company) || '—'}
                                                </span>
                                                <CccWorkspaceDuplicateIndicator flags={dupFlags} />
                                              </span>
                                              <span className="rec-lead-panel-meta">
                                                {(selectedLead?.contactName || lead.contactName) || '—'}
                                                {' · '}
                                                {prospeccaoSubstageLabel(selectedLead?.substage || lead.substage)}
                                              </span>
                                            </div>
                                            <button
                                              type="button"
                                              className="rec-lead-panel-collapse"
                                              onClick={() => setSelectedLeadId(null)}
                                              title="Fechar ficha"
                                            >
                                              <span>Fechar ficha</span>
                                              <span className="rec-lead-panel-collapse-icon" aria-hidden>
                                                ▼
                                              </span>
                                            </button>
                                          </div>

                                          <div className="rec-lead-panel-body">
                                            <div className="rec-lead-panel-main">
                                              <nav
                                                className="rec-lead-info-tabs"
                                                aria-label="Origem da informação"
                                              >
                                                {showAssinadoTab && (
                                                  <button
                                                    type="button"
                                                    className={`rec-lead-info-tab ${
                                                      leadInfoTab === 'assinado' ? 'is-active' : ''
                                                    }`}
                                                    onClick={() => setLeadInfoTab('assinado')}
                                                  >
                                                    Assinatura
                                                  </button>
                                                )}
                                                {showEnvioPropostaTab && (
                                                  <button
                                                    type="button"
                                                    className={`rec-lead-info-tab ${
                                                      leadInfoTab === 'envio_proposta'
                                                        ? 'is-active'
                                                        : ''
                                                    }`}
                                                    onClick={() => setLeadInfoTab('envio_proposta')}
                                                  >
                                                    Envio da Proposta
                                                  </button>
                                                )}
                                                {showTentativasTab && (
                                                  <button
                                                    type="button"
                                                    className={`rec-lead-info-tab ${
                                                      leadInfoTab === 'tentativas_contacto'
                                                        ? 'is-active'
                                                        : ''
                                                    }`}
                                                    onClick={() =>
                                                      setLeadInfoTab('tentativas_contacto')
                                                    }
                                                  >
                                                    Tentativas de Contacto
                                                    {tentativasIncomplete && (
                                                      <RecuperacaoMotivoWarningIcon
                                                        className="rec-lead-tab-warn"
                                                        title={TENTATIVAS_CONTACTO_WARNING_TEXT}
                                                      />
                                                    )}
                                                  </button>
                                                )}
                                                {showFidelizadosTab && (
                                                  <button
                                                    type="button"
                                                    className={`rec-lead-info-tab ${
                                                      leadInfoTab === 'fidelizados' ? 'is-active' : ''
                                                    }`}
                                                    onClick={() => setLeadInfoTab('fidelizados')}
                                                  >
                                                    Fidelização
                                                    {fidelizadosIncomplete && (
                                                      <RecuperacaoMotivoWarningIcon
                                                        className="rec-lead-tab-warn"
                                                        title={FIDELIZADOS_WARNING_TEXT}
                                                      />
                                                    )}
                                                  </button>
                                                )}
                                                {showMotivoTab && (
                                                  <button
                                                    type="button"
                                                    className={`rec-lead-info-tab ${
                                                      leadInfoTab === 'motivo' ? 'is-active' : ''
                                                    }`}
                                                    onClick={() => setLeadInfoTab('motivo')}
                                                  >
                                                    Motivo
                                                    {motivoIncomplete && (
                                                      <RecuperacaoMotivoWarningIcon
                                                        className="rec-lead-tab-warn"
                                                        title={
                                                          motivoVariant === 'pendente'
                                                            ? PENDENTE_MOTIVO_WARNING_TEXT
                                                            : motivoVariant === 'sem_interesse'
                                                              ? SEM_INTERESSE_MOTIVO_WARNING_TEXT
                                                              : MOTIVO_WARNING_TEXT
                                                        }
                                                      />
                                                    )}
                                                  </button>
                                                )}
                                                <button
                                                  type="button"
                                                  className={`rec-lead-info-tab ${
                                                    leadInfoTab === 'dados' ? 'is-active' : ''
                                                  }`}
                                                  onClick={() => setLeadInfoTab('dados')}
                                                >
                                                  Detalhes da Empresa
                                                </button>
                                                {selectedPedidoExists && (
                                                  <button
                                                    type="button"
                                                    className={`rec-lead-info-tab ${
                                                      leadInfoTab === 'pedido' ? 'is-active' : ''
                                                    }`}
                                                    onClick={() => setLeadInfoTab('pedido')}
                                                  >
                                                    Pedido de Proposta
                                                  </button>
                                                )}
                                                {canViewHistory && (
                                                  <button
                                                    type="button"
                                                    className={`rec-lead-info-tab ${
                                                      leadInfoTab === 'historico' ? 'is-active' : ''
                                                    }`}
                                                    onClick={() => setLeadInfoTab('historico')}
                                                  >
                                                    Histórico
                                                  </button>
                                                )}
                                              </nav>

                                              {leadInfoTab === 'historico' && canViewHistory ? (
                                                <CccProspeccaoLeadHistory
                                                  leadId={lead.id}
                                                  refreshKey={historyRefreshKey}
                                                />
                                              ) : leadInfoTab === 'pedido' && selectedPedidoExists ? (
                                                <RecuperacaoComercialRequests
                                                  requests={selectedPedidoLayout.comercialRequests}
                                                  expandedServicos={expandedPedidoServicos}
                                                  onToggleServico={(key) =>
                                                    setExpandedPedidoServicos((prev) => ({
                                                      ...prev,
                                                      [key]: !prev[key],
                                                    }))
                                                  }
                                                />
                                              ) : leadInfoTab === 'assinado' && showAssinadoTab ? (
                                                <ProspeccaoAssinadoForm
                                                  leadId={lead.id}
                                                  initial={selectedLead?.assinadoDados}
                                                  disabled={!isLeadMine(selectedLead || lead)}
                                                  onSaved={handleLeadFieldSaved}
                                                />
                                              ) : leadInfoTab === 'envio_proposta' && showEnvioPropostaTab ? (
                                                <ProspeccaoEnvioPropostaForm
                                                  leadId={lead.id}
                                                  defaultEmail={selectedLead?.email || lead.email}
                                                  initial={selectedLead?.envioProposta}
                                                  disabled={!isLeadMine(selectedLead || lead)}
                                                  onSaved={handleLeadFieldSaved}
                                                />
                                              ) : leadInfoTab === 'fidelizados' && showFidelizadosTab ? (
                                                <ProspeccaoFidelizadosForm
                                                  leadId={lead.id}
                                                  initial={selectedLead?.fidelizadosDados}
                                                  disabled={!isLeadMine(selectedLead || lead)}
                                                  onSaved={handleLeadFieldSaved}
                                                />
                                              ) : leadInfoTab === 'tentativas_contacto' &&
                                                showTentativasTab ? (
                                                <ProspeccaoTentativasContactoForm
                                                  leadId={lead.id}
                                                  initial={selectedLead?.tentativasContacto}
                                                  onSaved={handleTentativasSaved}
                                                />
                                              ) : leadInfoTab === 'motivo' &&
                                                showMotivoTab &&
                                                motivoVariant ? (
                                                <ProspeccaoWorkspaceMotivo
                                                  variant={motivoVariant}
                                                  leadId={lead.id}
                                                  initial={motivoData}
                                                  disabled={!isLeadMine(selectedLead || lead)}
                                                  onSaved={handleLeadFieldSaved}
                                                />
                                              ) : (
                                              <>
                                              <section className="rec-lead-detail-group prosp-lead-card">
                                                <h4 className="rec-lead-detail-group-title">
                                                  Informações da Lead
                                                </h4>
                                                <div className="rec-envio-proposta-fields prosp-lead-fields">
                                                  <label className="rec-envio-proposta-field">
                                                    <span className="rec-envio-proposta-label">Empresa</span>
                                                    <input
                                                      className="rec-envio-proposta-input"
                                                      value={selectedLead?.company || ''}
                                                      onChange={(e) =>
                                                        patchLead(lead.id, { company: e.target.value })
                                                      }
                                                    />
                                                  </label>
                                                  <label className="rec-envio-proposta-field">
                                                    <span className="rec-envio-proposta-label">Responsável</span>
                                                    <input
                                                      className="rec-envio-proposta-input"
                                                      value={selectedLead?.contactName || ''}
                                                      onChange={(e) =>
                                                        patchLead(lead.id, { contactName: e.target.value })
                                                      }
                                                    />
                                                  </label>
                                                  <label className="rec-envio-proposta-field">
                                                    <span className="rec-envio-proposta-label">
                                                      Contacto telefónico
                                                    </span>
                                                    <input
                                                      className="rec-envio-proposta-input"
                                                      value={selectedLead?.phone || ''}
                                                      onChange={(e) =>
                                                        patchLead(lead.id, { phone: e.target.value })
                                                      }
                                                    />
                                                  </label>
                                                  <label className="rec-envio-proposta-field">
                                                    <span className="rec-envio-proposta-label">Email</span>
                                                    <input
                                                      className="rec-envio-proposta-input"
                                                      value={selectedLead?.email || ''}
                                                      onChange={(e) =>
                                                        patchLead(lead.id, { email: e.target.value })
                                                      }
                                                    />
                                                  </label>
                                                  <div className="rec-envio-proposta-field prosp-lead-field-full">
                                                    <span className="rec-envio-proposta-label">Produto</span>
                                                    <div className="prosp-produto-options prosp-produto-options--detail">
                                                      {PROSPECCAO_PRODUTO_OPTIONS.map((opt) => (
                                                        <label
                                                          key={opt}
                                                          className="rec-envio-proposta-check prosp-produto-check"
                                                        >
                                                          <input
                                                            type="checkbox"
                                                            checked={(selectedLead?.produto || []).includes(opt)}
                                                            onChange={() =>
                                                              patchLead(lead.id, {
                                                                produto: toggleProspeccaoProduto(
                                                                  selectedLead?.produto || [],
                                                                  opt
                                                                ),
                                                              })
                                                            }
                                                          />
                                                          {opt}
                                                        </label>
                                                      ))}
                                                    </div>
                                                  </div>
                                                  <label className="rec-envio-proposta-field prosp-lead-field-full">
                                                    <span className="rec-envio-proposta-label">
                                                      Link do Google Maps
                                                    </span>
                                                    <input
                                                      className="rec-envio-proposta-input"
                                                      value={selectedLead?.googleMapsLink || ''}
                                                      onChange={(e) =>
                                                        patchLead(lead.id, { googleMapsLink: e.target.value })
                                                      }
                                                      placeholder="https://maps.google.com/..."
                                                    />
                                                  </label>
                                                </div>
                                              </section>

                                              <div className="prosp-detail-actions">
                                                <button
                                                  type="button"
                                                  className="rec-btn rec-btn-primary"
                                                  onClick={() => void saveLead(selectedLead || lead)}
                                                >
                                                  Guardar dados
                                                </button>
                                              </div>
                                              </>
                                              )}
                                            </div>

                                            <aside className="rec-lead-sidebar">
                                              <div className="rec-lead-workboard">
                                                <h4 className="rec-lead-section-title">Ações</h4>
                                                <div className="rec-lead-workboard-row">
                                                  <label className="rec-lead-field">
                                                    <span>Alterar estado</span>
                                                    <select
                                                      className="rec-select"
                                                      value={selectedLead?.substage || lead.substage}
                                                      onChange={(e) =>
                                                        void changeLeadSubstage(lead.id, e.target.value)
                                                      }
                                                    >
                                                      {PROSPECCAO_SECTIONS.map((section) => (
                                                        <optgroup
                                                          key={section.key}
                                                          label={section.title}
                                                        >
                                                          {section.subsections.map((subOption) => (
                                                            <option
                                                              key={subOption.substage}
                                                              value={subOption.substage}
                                                            >
                                                              {subOption.label}
                                                            </option>
                                                          ))}
                                                        </optgroup>
                                                      ))}
                                                    </select>
                                                  </label>
                                                  <button
                                                    type="button"
                                                    className="rec-btn rec-btn-criar-evento prosp-lead-action"
                                                    onClick={() =>
                                                      openCreateCalendarEvent(selectedLead || lead)
                                                    }
                                                  >
                                                    Criar Evento
                                                  </button>
                                                  <div className="prosp-proposal-actions-row">
                                                    <button
                                                      type="button"
                                                      className="rec-btn rec-btn-solicitar-proposta prosp-lead-action prosp-lead-action--half"
                                                      disabled={
                                                        !isLeadMine(selectedLead || lead) ||
                                                        preparingProposta ||
                                                        preparingRefazer
                                                      }
                                                      onClick={() =>
                                                        void handleSolicitarProposta(selectedLead || lead)
                                                      }
                                                    >
                                                      {preparingProposta
                                                        ? 'A preparar…'
                                                        : 'Solicitar Proposta'}
                                                    </button>
                                                    <button
                                                      type="button"
                                                      className="rec-btn rec-btn-refazer-proposta prosp-lead-action prosp-lead-action--half"
                                                      disabled={preparingRefazer || preparingProposta}
                                                      onClick={() =>
                                                        void handleRefazerProposta(selectedLead || lead)
                                                      }
                                                    >
                                                      {preparingRefazer ? 'A preparar…' : 'Refazer'}
                                                    </button>
                                                  </div>
                                                  {normalizeProspeccaoSubstage(
                                                    selectedLead?.substage || lead.substage
                                                  ) === PROSPECCAO_SUBSTAGE_CONTRATO_SUBMETIDO && (
                                                    <button
                                                      type="button"
                                                      className="rec-btn rec-btn-submeter-contrato prosp-lead-action"
                                                      disabled={
                                                        !isLeadMine(selectedLead || lead) ||
                                                        preparingContrato
                                                      }
                                                      onClick={() =>
                                                        void handleSubmeterContrato(selectedLead || lead)
                                                      }
                                                    >
                                                      {preparingContrato
                                                        ? 'A preparar…'
                                                        : 'Submeter Contrato'}
                                                    </button>
                                                  )}
                                                  <button
                                                    type="button"
                                                    className="rec-btn rec-btn-ghost prosp-lead-action prosp-lead-action--danger"
                                                    onClick={() =>
                                                      confirmRemoveLead(selectedLead || lead)
                                                    }
                                                  >
                                                    Remover lead
                                                  </button>
                                                </div>
                                              </div>
                                              <div className="rec-lead-notes">
                                                <h4 className="rec-lead-section-title">Notas</h4>
                                                <div className="rec-lead-notes-compose">
                                                  <textarea
                                                    className="rec-lead-notes-input"
                                                    rows={3}
                                                    placeholder="Registar contacto, feedback do cliente, próximos passos…"
                                                    value={leadNoteDrafts[lead.id] || ''}
                                                    onChange={(e) =>
                                                      setLeadNoteDrafts((prev) => ({
                                                        ...prev,
                                                        [lead.id]: e.target.value,
                                                      }))
                                                    }
                                                  />
                                                  <button
                                                    type="button"
                                                    className="rec-btn rec-btn-primary"
                                                    disabled={
                                                      !(leadNoteDrafts[lead.id] || '').trim() ||
                                                      savingLeadNoteId === lead.id
                                                    }
                                                    onClick={() => void saveLeadNote(lead.id)}
                                                  >
                                                    {savingLeadNoteId === lead.id
                                                      ? 'A guardar…'
                                                      : 'Adicionar nota'}
                                                  </button>
                                                </div>

                                                <div className="rec-lead-notes-list">
                                                  {(leadNotes[lead.id] || []).length === 0 ? (
                                                    <p className="rec-lead-notes-empty">
                                                      Ainda sem notas nesta lead.
                                                    </p>
                                                  ) : (
                                                    [...(leadNotes[lead.id] || [])].reverse().map((note, index) => {
                                                      const noteId = note.id || `${note.at}-${index}`;
                                                      const editKey = leadNoteKey(lead.id, noteId);
                                                      const isEditing = editingLeadNoteKey === editKey;
                                                      const isMutating = mutatingLeadNoteKey === editKey;
                                                      const canModify = canModifyLeadNote(note);
                                                      return (
                                                        <article
                                                          key={noteId}
                                                          className="rec-lead-note-card"
                                                        >
                                                          {isEditing ? (
                                                            <>
                                                              <textarea
                                                                className="rec-lead-notes-input rec-lead-note-edit-input"
                                                                rows={3}
                                                                value={editingLeadNoteText}
                                                                onChange={(e) =>
                                                                  setEditingLeadNoteText(e.target.value)
                                                                }
                                                              />
                                                              <div className="rec-lead-note-actions">
                                                                <button
                                                                  type="button"
                                                                  className="rec-btn rec-btn-primary rec-btn-sm"
                                                                  disabled={
                                                                    !editingLeadNoteText.trim() || isMutating
                                                                  }
                                                                  onClick={() =>
                                                                    void saveEditLeadNote(lead.id, noteId)
                                                                  }
                                                                >
                                                                  {isMutating ? 'A guardar…' : 'Guardar'}
                                                                </button>
                                                                <button
                                                                  type="button"
                                                                  className="rec-btn rec-btn-ghost rec-btn-sm"
                                                                  disabled={isMutating}
                                                                  onClick={cancelEditLeadNote}
                                                                >
                                                                  Cancelar
                                                                </button>
                                                              </div>
                                                            </>
                                                          ) : (
                                                            <>
                                                              <p className="rec-lead-note-text">{note.text}</p>
                                                              <footer className="rec-lead-note-meta">
                                                                {formatLeadNoteMeta(note)}
                                                              </footer>
                                                              {canModify && note.id ? (
                                                                <div className="rec-lead-note-actions">
                                                                  <button
                                                                    type="button"
                                                                    className="rec-btn rec-btn-ghost rec-btn-sm"
                                                                    disabled={Boolean(mutatingLeadNoteKey)}
                                                                    onClick={() =>
                                                                      startEditLeadNote(lead.id, note)
                                                                    }
                                                                  >
                                                                    Editar
                                                                  </button>
                                                                  <button
                                                                    type="button"
                                                                    className="rec-btn rec-btn-ghost rec-btn-sm rec-lead-note-delete"
                                                                    disabled={Boolean(mutatingLeadNoteKey)}
                                                                    onClick={() =>
                                                                      confirmDeleteLeadNote(lead.id, note)
                                                                    }
                                                                  >
                                                                    Eliminar
                                                                  </button>
                                                                </div>
                                                              ) : null}
                                                            </>
                                                          )}
                                                        </article>
                                                      );
                                                    })
                                                  )}
                                                </div>
                                              </div>
                                            </aside>
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {totalRows > LEADS_PER_PAGE && (
                        <nav
                          className="prosp-pagination"
                          aria-label={`Paginação — ${sub.label}`}
                        >
                          <button
                            type="button"
                            className="rec-btn rec-btn-ghost prosp-pagination-btn"
                            disabled={currentPage <= 1}
                            onClick={() => setSubsectionPage(sub.substage, currentPage - 1)}
                          >
                            Anterior
                          </button>
                          <span className="prosp-pagination-meta">
                            A mostrar {pageStart}–{pageEnd} de {totalRows} · Página {currentPage} de{' '}
                            {totalPages}
                          </span>
                          <button
                            type="button"
                            className="rec-btn rec-btn-ghost prosp-pagination-btn"
                            disabled={currentPage >= totalPages}
                            onClick={() => setSubsectionPage(sub.substage, currentPage + 1)}
                          >
                            Seguinte
                          </button>
                        </nav>
                      )}
                      </>
                    )}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </div>

      {showCreate && (
        <div className="rec-modal-backdrop" onClick={() => setShowCreate(false)} role="presentation">
          <div
            className="rec-modal prosp-create-modal prospeccao-create-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="prospeccao-create-modal-title"
          >
            <div className="rec-modal-head prosp-create-modal-head prospeccao-create-modal-head">
              <div className="prosp-create-head-wrap">
                <h2 id="prospeccao-create-modal-title" className="rec-modal-title">
                  Criar Lead
                </h2>
                <p className="prospeccao-create-subtitle">
                  Registe uma nova lead de prospeção. Campos com * são obrigatórios.
                </p>
              </div>
              <button
                type="button"
                className="prosp-create-close"
                onClick={() => setShowCreate(false)}
                aria-label="Fechar modal"
              >
                ×
              </button>
            </div>
            <div className="rec-modal-body prosp-create-modal-body prospeccao-create-modal-body">
              <label className="prospeccao-create-field">
                <span className="prosp-create-label-title">
                  Nome da Empresa <span className="rec-required-asterisk">*</span>
                </span>
                <input
                  className="rec-input"
                  value={draft.company}
                  onChange={(e) => setDraft((prev) => ({ ...prev, company: e.target.value }))}
                />
              </label>
              <label className="prospeccao-create-field">
                <span className="prosp-create-label-title">
                  Responsável da Empresa <span className="rec-required-asterisk">*</span>
                </span>
                <input
                  className="rec-input"
                  value={draft.contactName}
                  onChange={(e) => setDraft((prev) => ({ ...prev, contactName: e.target.value }))}
                />
              </label>
              <label className="prospeccao-create-field">
                <span className="prosp-create-label-title">
                  Contacto <span className="rec-required-asterisk">*</span>
                </span>
                <input
                  className="rec-input"
                  value={draft.phone}
                  onChange={(e) => setDraft((prev) => ({ ...prev, phone: e.target.value }))}
                />
              </label>
              <label className="prospeccao-create-field">
                <span className="prosp-create-label-title">E-mail</span>
                <input
                  className="rec-input"
                  value={draft.email}
                  onChange={(e) => setDraft((prev) => ({ ...prev, email: e.target.value }))}
                />
              </label>
              <ProspeccaoProdutoField
                value={draft.produto}
                onChange={(produto) => setDraft((prev) => ({ ...prev, produto }))}
                fullWidth
              />
              <label className="prospeccao-create-field prospeccao-create-field--full">
                <span className="prosp-create-label-title">Link do Google Maps</span>
                <input
                  className="rec-input"
                  value={draft.googleMapsLink}
                  onChange={(e) => setDraft((prev) => ({ ...prev, googleMapsLink: e.target.value }))}
                  placeholder="https://maps.google.com/..."
                />
              </label>
              <div className="rec-modal-actions prospeccao-create-modal-actions">
                <button type="button" className="rec-btn rec-btn-ghost" onClick={() => setShowCreate(false)}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="rec-btn rec-btn-primary prospeccao-create-submit"
                  onClick={() => void createLead()}
                >
                  Criar Lead
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <CccCalendarEventModal
        open={calendarModalOpen}
        initialStart={calendarSlot.start}
        initialEnd={calendarSlot.end}
        initialClient={calendarLead ? buildProspeccaoCalendarClient(calendarLead) : null}
        lockClient={!!calendarLead}
        defaultTitle={calendarLead ? `Contacto — ${calendarLead.company}` : ''}
        onClose={() => {
          setCalendarModalOpen(false);
          setCalendarLead(null);
        }}
        onSaved={() => {
          setCalendarModalOpen(false);
          setCalendarLead(null);
        }}
      />

      {gestaoModal && (
        <CccGestaoModalsOverlay
          key={`${gestaoModal}-${gestaoModalNonce}-${gestaoFilterUserId ?? 'none'}`}
          modal={gestaoModal}
          filterUserId={gestaoFilterUserId}
          filterUserLabel={workspaceSelectionLabel}
          onClose={() => setGestaoModal(null)}
        />
      )}

      {showPedirAssinaturaDigital && (
        <CccPedirAssinaturaDigitalOverlay
          key={`pedir-assinatura-${pedirAssinaturaNonce}`}
          onClose={() => setShowPedirAssinaturaDigital(false)}
        />
      )}

      {canManageWorkspaceScope && (
        <Drawer
          title={<Text strong style={{ fontSize: 20 }}>Equipa</Text>}
          placement="left"
          closable
          onClose={() => setDrawerOpen(false)}
          open={drawerOpen}
          width={isMobileOrTablet ? '80vw' : 300}
          style={{
            borderTopRightRadius: 18,
            borderBottomRightRadius: 18,
          }}
          headerStyle={{
            borderBottom: '1px solid #f0f0f0',
            background: '#fff',
            borderTopRightRadius: 18,
          }}
          bodyStyle={{
            padding: '16px',
            background: '#fff',
            maxHeight: '100vh',
            overflowY: 'auto',
          }}
        >
          <div className="search-section" style={{ marginBottom: '16px' }}>
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder="Pesquisar utilizador..."
                value={sidebarSearchTerm}
                onChange={(e) => setSidebarSearchTerm(e.target.value)}
                className="user-search-input"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d9d9d9',
                }}
              />
            </div>
          </div>

          <div className="user-list">
            <div
              className={`user-item ${workspaceUserId === 'all' || workspaceUserId === null ? 'selected' : ''}`}
              onClick={() => {
                setWorkspaceUserId('all');
                setDrawerOpen(false);
              }}
              style={{
                cursor: 'pointer',
                padding: '12px',
                marginBottom: '8px',
                borderRadius: '8px',
                border:
                  workspaceUserId === 'all' || workspaceUserId === null
                    ? '2px solid #1890ff'
                    : '1px solid #f0f0f0',
                backgroundColor:
                  workspaceUserId === 'all' || workspaceUserId === null ? '#e6f7ff' : '#fff',
                transition: 'all 0.2s',
              }}
            >
              <div className="user-info" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div
                  className="user-color"
                  style={{
                    backgroundColor: '#1890ff',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    flexShrink: 0,
                  }}
                />
                <span className="user-name" style={{ flex: 1, fontWeight: 500 }}>
                  Toda a Equipa
                </span>
              </div>
            </div>

            {filteredSidebarUsers.map((option) => (
              <div
                key={option.id}
                className={`user-item ${workspaceUserId === option.id ? 'selected' : ''}`}
                onClick={() => {
                  setWorkspaceUserId(option.id);
                  setDrawerOpen(false);
                }}
                style={{
                  cursor: 'pointer',
                  padding: '12px',
                  marginBottom: '8px',
                  borderRadius: '8px',
                  border: workspaceUserId === option.id ? '2px solid #1890ff' : '1px solid #f0f0f0',
                  backgroundColor: workspaceUserId === option.id ? '#e6f7ff' : '#fff',
                  transition: 'all 0.2s',
                }}
              >
                <div className="user-info" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div
                    className="user-color"
                    style={{
                      backgroundColor: option.id % 2 === 0 ? '#ff001e' : '#cccccc',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      flexShrink: 0,
                    }}
                  />
                  <span className="user-name" style={{ flex: 1, fontWeight: 500 }}>
                    {option.name || option.username || 'Unknown'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Drawer>
      )}

      {canReassignComercial && (
        <CccAlterarComercialModal
          open={alterarComercialOpen}
          users={workspaceUsers}
          submitting={alterarComercialSubmitting}
          onClose={() => !alterarComercialSubmitting && setAlterarComercialOpen(false)}
          onConfirm={handleConfirmAlterarComercial}
        />
      )}

      {showSolicitarProposta && solicitarPropostaLead && solicitarPropostaLead.marcacaoId && (
        <CccSolicitarPropostaProspeccaoOverlay
          mode="solicitar"
          marcacaoId={solicitarPropostaLead.marcacaoId}
          lead={{
            id: solicitarPropostaLead.id,
            company: solicitarPropostaLead.company,
            contactName: solicitarPropostaLead.contactName,
            phone: solicitarPropostaLead.phone,
            email: solicitarPropostaLead.email,
            googleMapsLink: solicitarPropostaLead.googleMapsLink,
          }}
          onClose={() => {
            setShowSolicitarProposta(false);
            setSolicitarPropostaLead(null);
          }}
          onSuccess={() => void handleProposalSuccess()}
        />
      )}

      {showRefazerProposta && refazerPropostaLead && refazerPropostaLead.marcacaoId && (
        <CccSolicitarPropostaProspeccaoOverlay
          mode="refazer"
          marcacaoId={refazerPropostaLead.marcacaoId}
          lead={{
            id: refazerPropostaLead.id,
            company: refazerPropostaLead.company,
            contactName: refazerPropostaLead.contactName,
            phone: refazerPropostaLead.phone,
            email: refazerPropostaLead.email,
            googleMapsLink: refazerPropostaLead.googleMapsLink,
          }}
          onClose={() => {
            setShowRefazerProposta(false);
            setRefazerPropostaLead(null);
          }}
          onSuccess={() => void handleProposalSuccess()}
        />
      )}

      {showSubmeterContrato && submeterContratoLead && submeterContratoLead.marcacaoId && (
        <CccSubmeterContratoProspeccaoOverlay
          marcacaoId={submeterContratoLead.marcacaoId}
          lead={{
            id: submeterContratoLead.id,
            company: submeterContratoLead.company,
            contactName: submeterContratoLead.contactName,
            phone: submeterContratoLead.phone,
            email: submeterContratoLead.email,
            googleMapsLink: submeterContratoLead.googleMapsLink,
          }}
          onClose={() => setShowSubmeterContrato(false)}
          onSuccess={() => void handleContractSuccess()}
        />
      )}

      <RecuperacaoInvestigateModal
        open={investigateOpen}
        onClose={closeInvestigateModal}
        onOpenMarcacao={(row) => void handleInvestigateOpenMarcacao(row)}
        quickSearchSeed={investigateQuickSearch}
      />
    </div>
  );
};

export default ProspeccaoBoard;

